import React, { startTransition, useCallback, useDeferredValue, useMemo, useState } from 'react';
import {
  BookOpen,
  CalendarDays,
  Filter,
  Library,
  MapPin,
  Search,
  ShieldAlert,
} from 'lucide-react';
import './EquipmentLendingLibrary.css';
import EquipmentReservationModal from '../components/equipmentLibrary/EquipmentReservationModal';
import EquipmentVerificationPanel from '../components/equipmentLibrary/EquipmentVerificationPanel';
import {
  CONTAMINANTS,
  RESERVATION_STATUSES,
} from '../data/equipmentLendingLibrary';
import {
  findReservation,
  getEquipmentLibrarySnapshot,
  markReservationPickedUp,
  markReservationReturned,
  reserveEquipment,
} from '../services/equipmentLendingService';

function formatStatusFromCounts(availableCount, lowThreshold = 2) {
  if (availableCount <= 0) {
    return { label: 'Unavailable', tone: 'unavailable' };
  }

  if (availableCount <= lowThreshold) {
    return { label: 'Low availability', tone: 'low' };
  }

  return { label: 'Available', tone: 'available' };
}

function matchesSearch(searchValue, texts) {
  if (!searchValue) {
    return true;
  }

  return texts.some((text) => String(text || '').toLowerCase().includes(searchValue));
}

export default function EquipmentLendingLibrary() {
  const [libraryData, setLibraryData] = useState(() => getEquipmentLibrarySnapshot());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEquipmentId, setSelectedEquipmentId] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [isReservationOpen, setIsReservationOpen] = useState(false);
  const [reservationEquipmentId, setReservationEquipmentId] = useState('');
  const [reservationConfirmation, setReservationConfirmation] = useState(null);
  const [submittingReservation, setSubmittingReservation] = useState(false);
  const deferredSearchQuery = useDeferredValue(searchQuery.trim().toLowerCase());

  const refreshLibrary = useCallback(() => {
    const snapshot = getEquipmentLibrarySnapshot();
    startTransition(() => setLibraryData(snapshot));
  }, []);

  const { equipment, locations, inventory, reservations } = libraryData;

  const equipmentById = useMemo(
    () => Object.fromEntries(equipment.map((item) => [item.id, item])),
    [equipment],
  );
  const locationById = useMemo(
    () => Object.fromEntries(locations.map((item) => [item.id, item])),
    [locations],
  );
  const contaminantById = useMemo(
    () => Object.fromEntries(CONTAMINANTS.map((item) => [item.id, item])),
    [],
  );

  const inventoryByEquipmentId = useMemo(() => {
    const nextMap = {};
    inventory.forEach((item) => {
      if (!nextMap[item.equipmentId]) {
        nextMap[item.equipmentId] = [];
      }
      nextMap[item.equipmentId].push(item);
    });
    return nextMap;
  }, [inventory]);

  const inventoryByLocationId = useMemo(() => {
    const nextMap = {};
    inventory.forEach((item) => {
      if (!nextMap[item.locationId]) {
        nextMap[item.locationId] = [];
      }
      nextMap[item.locationId].push(item);
    });
    return nextMap;
  }, [inventory]);

  const stats = useMemo(() => {
    const totalAvailable = inventory.reduce((sum, item) => sum + item.availableCount, 0);
    const totalOnLoan = inventory.reduce((sum, item) => sum + item.onLoanCount, 0);
    const activeHolds = reservations.filter((item) => item.status === RESERVATION_STATUSES.HELD).length;

    return {
      totalAvailable,
      totalOnLoan,
      activeHolds,
      siteCount: locations.length,
    };
  }, [inventory, locations.length, reservations]);

  const filteredEquipment = useMemo(() => {
    return equipment
      .filter((item) => {
        if (selectedEquipmentId && item.id !== selectedEquipmentId) {
          return false;
        }

        const relatedInventory = inventoryByEquipmentId[item.id] || [];
        if (selectedLocationId && !relatedInventory.some((entry) => entry.locationId === selectedLocationId)) {
          return false;
        }

        const relatedLocations = relatedInventory.map((entry) => locationById[entry.locationId]?.name || '');
        const contaminant = contaminantById[item.contaminantId];

        return matchesSearch(deferredSearchQuery, [
          item.name,
          item.detects,
          item.healthImportance,
          item.recommendedTestingLocation,
          item.safetyNote,
          contaminant?.name,
          contaminant?.summary,
          ...relatedLocations,
        ]);
      })
      .sort((left, right) => {
        const leftAvailable = (inventoryByEquipmentId[left.id] || []).reduce((sum, item) => sum + item.availableCount, 0);
        const rightAvailable = (inventoryByEquipmentId[right.id] || []).reduce((sum, item) => sum + item.availableCount, 0);
        return rightAvailable - leftAvailable;
      });
  }, [
    contaminantById,
    deferredSearchQuery,
    equipment,
    inventoryByEquipmentId,
    locationById,
    selectedEquipmentId,
    selectedLocationId,
  ]);

  const filteredLocations = useMemo(() => {
    return locations
      .filter((location) => {
        if (selectedLocationId && location.id !== selectedLocationId) {
          return false;
        }

        const locationInventory = inventoryByLocationId[location.id] || [];
        if (selectedEquipmentId && !locationInventory.some((entry) => entry.equipmentId === selectedEquipmentId)) {
          return false;
        }

        const equipmentNames = locationInventory.map((entry) => equipmentById[entry.equipmentId]?.name || entry.equipmentId);
        return matchesSearch(deferredSearchQuery, [
          location.name,
          location.address,
          location.openingHours.join(' '),
          ...equipmentNames,
        ]);
      })
      .sort((left, right) => {
        const leftAvailable = (inventoryByLocationId[left.id] || []).reduce((sum, item) => sum + item.availableCount, 0);
        const rightAvailable = (inventoryByLocationId[right.id] || []).reduce((sum, item) => sum + item.availableCount, 0);
        return rightAvailable - leftAvailable;
      });
  }, [
    deferredSearchQuery,
    equipmentById,
    inventoryByLocationId,
    locations,
    selectedEquipmentId,
    selectedLocationId,
  ]);

  const handleOpenReservation = (equipmentId = '') => {
    setReservationEquipmentId(equipmentId || selectedEquipmentId || '');
    setReservationConfirmation(null);
    setIsReservationOpen(true);
  };

  const handleCloseReservation = () => {
    setIsReservationOpen(false);
    setReservationConfirmation(null);
  };

  const handleSubmitReservation = async (payload) => {
    setSubmittingReservation(true);

    try {
      const reservation = reserveEquipment(payload);
      refreshLibrary();
      setReservationConfirmation(reservation);
      return reservation;
    } finally {
      setSubmittingReservation(false);
    }
  };

  const handleVerifyReservation = async (identifier) => {
    return findReservation(identifier);
  };

  const handleMarkPickedUp = async (reservationId) => {
    const reservation = markReservationPickedUp(reservationId);
    refreshLibrary();
    return reservation;
  };

  const handleMarkReturned = async (reservationId) => {
    const reservation = markReservationReturned(reservationId);
    refreshLibrary();
    return reservation;
  };

  return (
    <div className="equipment-library">
      <section className="equipment-library__hero">
        <div>
          <div className="equipment-library__eyebrow">
            <Library size={16} aria-hidden="true" />
            Community screening program
          </div>
          <h2>Equipment Lending Library</h2>
          <p className="equipment-library__hero-copy">
            Borrow detectors and screening tools to explore possible home and environmental exposures linked to
            cancer risk or respiratory health concerns. Community members can place holds, pick up equipment from
            partner sites such as libraries and community centres, and learn when professional follow-up is needed.
          </p>

          <div className="equipment-library__hero-actions">
            <button
              type="button"
              className="equipment-library__primary-button"
              onClick={() => handleOpenReservation(selectedEquipmentId)}
            >
              Reserve equipment
            </button>
            <button
              type="button"
              className="equipment-library__secondary-button"
              onClick={refreshLibrary}
            >
              Refresh availability
            </button>
          </div>

          <div className="equipment-library__hero-note">
            Use the filters below to browse by device type or pickup site, then place a hold using the integrated
            reservation flow.
          </div>
        </div>

        <div className="equipment-library__stats-grid" aria-label="Equipment lending summary">
          <div className="equipment-library__stat-card">
            <strong>{stats.totalAvailable}</strong>
            <span>Devices available now</span>
          </div>
          <div className="equipment-library__stat-card">
            <strong>{stats.totalOnLoan}</strong>
            <span>Devices currently on loan</span>
          </div>
          <div className="equipment-library__stat-card">
            <strong>{stats.activeHolds}</strong>
            <span>Open holds awaiting pickup</span>
          </div>
          <div className="equipment-library__stat-card">
            <strong>{stats.siteCount}</strong>
            <span>Pickup sites in the mock network</span>
          </div>
        </div>
      </section>

      <section className="equipment-library__filters-card">
        <div className="equipment-library__section-heading">
          <div>
            <div className="equipment-library__eyebrow">
              <Filter size={16} aria-hidden="true" />
              Find equipment
            </div>
            <h3>Browse by contaminant, site, or device</h3>
          </div>
        </div>

        <div className="equipment-library__toolbar">
          <label className="equipment-library__field">
            <span>
              <Search size={14} aria-hidden="true" style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              Search
            </span>
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search radon, mold, Kingston, lead, water..."
              aria-label="Search equipment lending inventory"
            />
          </label>

          <label className="equipment-library__field">
            <span>Equipment type</span>
            <select
              value={selectedEquipmentId}
              onChange={(event) => setSelectedEquipmentId(event.target.value)}
              aria-label="Filter by equipment type"
            >
              <option value="">All equipment</option>
              {equipment.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>

          <label className="equipment-library__field">
            <span>Pickup location</span>
            <select
              value={selectedLocationId}
              onChange={(event) => setSelectedLocationId(event.target.value)}
              aria-label="Filter by pickup location"
            >
              <option value="">All locations</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="equipment-library__section-card">
        <div className="equipment-library__section-heading">
          <div>
            <div className="equipment-library__eyebrow">
              <BookOpen size={16} aria-hidden="true" />
              Educational section
            </div>
            <h3>Learn what each tool helps screen for</h3>
          </div>
        </div>

        <p className="equipment-library__section-copy">
          These community lending tools help identify possible home contaminants linked to cancer or respiratory
          health concerns. They are intended to support screening, awareness, and next-step decisions.
        </p>

        {filteredEquipment.length === 0 ? (
          <div className="equipment-library__empty-state">
            <strong>No equipment matches these filters.</strong>
            <p>Try clearing the search or choosing a different location to see more lending options.</p>
          </div>
        ) : (
          <div className="equipment-library__card-grid">
            {filteredEquipment.map((item) => {
              const contaminant = contaminantById[item.contaminantId];
              const relatedInventory = inventoryByEquipmentId[item.id] || [];
              const totalAvailable = relatedInventory.reduce((sum, entry) => sum + entry.availableCount, 0);
              const availableSiteCount = relatedInventory.filter((entry) => entry.availableCount > 0).length;

              return (
                <article key={item.id} className="equipment-library__equipment-card">
                  <div className="equipment-library__equipment-card-header">
                    <div>
                      <div className="equipment-library__subtle-label">{contaminant?.name || 'Environmental screening'}</div>
                      <h4>{item.name}</h4>
                    </div>
                    <div className={`equipment-library__status-badge equipment-library__status-badge--${formatStatusFromCounts(totalAvailable).tone}`}>
                      {formatStatusFromCounts(totalAvailable).label}
                    </div>
                  </div>

                  <div className="equipment-library__meta-list">
                    <div className="equipment-library__meta-item">
                      <strong>What it detects</strong>
                      <p>{item.detects}</p>
                    </div>
                    <div className="equipment-library__meta-item">
                      <strong>Why it matters for health</strong>
                      <p>{item.healthImportance}</p>
                    </div>
                    <div className="equipment-library__meta-item">
                      <strong>Recommended testing location</strong>
                      <p>{item.recommendedTestingLocation}</p>
                    </div>
                    <div className="equipment-library__meta-item">
                      <strong>Basic safety note</strong>
                      <p>{item.safetyNote}</p>
                    </div>
                  </div>

                  <div className="equipment-library__card-footer">
                    <div className="equipment-library__availability-copy">
                      {totalAvailable} available across {availableSiteCount} pickup site{availableSiteCount === 1 ? '' : 's'}
                    </div>
                    <button
                      type="button"
                      className="equipment-library__primary-button"
                      onClick={() => handleOpenReservation(item.id)}
                      disabled={totalAvailable <= 0}
                    >
                      Borrow this device
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className="equipment-library__section-card">
        <div className="equipment-library__section-heading">
          <div>
            <div className="equipment-library__eyebrow">
              <MapPin size={16} aria-hidden="true" />
              Inventory and locations
            </div>
            <h3>Live mock availability by pickup site</h3>
          </div>
        </div>

        <p className="equipment-library__section-copy">
          This mock inventory uses localStorage-backed state so reservations, pickups, and returns update counts in real time.
        </p>

        {filteredLocations.length === 0 ? (
          <div className="equipment-library__empty-state">
            <strong>No pickup sites match the current filters.</strong>
            <p>Clear one or more filters to review the full lending network.</p>
          </div>
        ) : (
          <div className="equipment-library__location-list">
            {filteredLocations.map((location) => {
              const visibleInventory = (inventoryByLocationId[location.id] || []).filter((entry) => (
                !selectedEquipmentId || entry.equipmentId === selectedEquipmentId
              ));
              const totalAvailable = visibleInventory.reduce((sum, entry) => sum + entry.availableCount, 0);
              const totalOnLoan = visibleInventory.reduce((sum, entry) => sum + entry.onLoanCount, 0);
              const locationStatus = formatStatusFromCounts(totalAvailable, 3);

              return (
                <article key={location.id} className="equipment-library__location-card">
                  <div className="equipment-library__location-card-header">
                    <div>
                      <div className="equipment-library__subtle-label">Pickup site</div>
                      <h4>{location.name}</h4>
                    </div>
                    <div className={`equipment-library__status-badge equipment-library__status-badge--${locationStatus.tone}`}>
                      {locationStatus.label}
                    </div>
                  </div>

                  <div className="equipment-library__location-meta">
                    <div className="equipment-library__address-block">
                      <strong>
                        <MapPin size={14} aria-hidden="true" style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                        Address
                      </strong>
                      <p>{location.address}</p>
                    </div>
                    <div className="equipment-library__hours-block">
                      <strong>
                        <CalendarDays size={14} aria-hidden="true" style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                        Opening hours
                      </strong>
                      <p>{location.openingHours.join(' | ')}</p>
                    </div>
                  </div>

                  <div className="equipment-library__inventory-list">
                    {visibleInventory.map((entry) => {
                      const entryStatus = formatStatusFromCounts(entry.availableCount);
                      const device = equipmentById[entry.equipmentId];

                      return (
                        <div key={entry.id} className="equipment-library__inventory-row">
                          <div>
                            <strong>{device?.name || entry.equipmentId}</strong>
                            <span>{entry.totalCount} total in circulation at this site</span>
                          </div>
                          <span>{entry.availableCount} available</span>
                          <span>{entry.onLoanCount} on loan</span>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <div className={`equipment-library__status-badge equipment-library__status-badge--${entryStatus.tone}`}>
                              {entryStatus.label}
                            </div>
                            <button
                              type="button"
                              className="equipment-library__inline-action"
                              onClick={() => handleOpenReservation(entry.equipmentId)}
                              disabled={entry.availableCount <= 0}
                            >
                              Reserve
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="equipment-library__card-footer">
                    <div className="equipment-library__availability-copy">
                      {totalAvailable} available and {totalOnLoan} on loan across the displayed equipment.
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <EquipmentVerificationPanel
        equipmentById={equipmentById}
        locationById={locationById}
        onVerify={handleVerifyReservation}
        onPickup={handleMarkPickedUp}
        onReturn={handleMarkReturned}
      />

      <section className="equipment-library__disclaimer">
        <strong>
          <ShieldAlert size={16} aria-hidden="true" style={{ verticalAlign: 'middle', marginRight: '6px' }} />
          Safety and legal disclaimer
        </strong>
        <p>
          This program is for educational and screening purposes only. It does not replace professional environmental
          testing, medical advice, or certified remediation services. If elevated levels or hazards are detected,
          users should contact qualified professionals.
        </p>
      </section>

      <EquipmentReservationModal
        open={isReservationOpen}
        equipmentCatalog={equipment}
        locations={locations}
        inventory={inventory}
        initialEquipmentId={reservationEquipmentId}
        reservation={reservationConfirmation}
        submitting={submittingReservation}
        onClose={handleCloseReservation}
        onSubmit={handleSubmitReservation}
      />
    </div>
  );
}
